import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X } from 'lucide-react';
import SignUpForm from '../signupforms/SignUpForm';
import SignUpFormWithDownload from '../signupforms/SignUpFormWithDownload';
import SignUpForDemo from '../signupforms/SignUpForDemo';
import SignUpForWebinar from '../signupforms/SignUpForWebinar';
import { FileMetadata } from '@/models/Post';

interface SignUpModalProps {
    isOpen: boolean;
    onClose: () => void;
    formType?: 'general' | 'download' | 'demo' | 'webinar';
    downloadFile?: FileMetadata;
}

export default function SignUpModal({ 
    isOpen, 
    onClose, 
    formType = 'general',
    downloadFile 
}: SignUpModalProps) {
    const getTitle = () => {
        switch (formType) {
            case 'download':
                return 'Download Lesson Plan';
            case 'demo':
                return 'Get Demo Access';
            case 'webinar':
                return 'Register for Webinar';
            default:
                return 'Join EmploSkills';
        }
    };

    const getForm = () => {
        switch (formType) {
            case 'download':
                return <SignUpFormWithDownload onSuccess={onClose} downloadFile={downloadFile!} />;
            case 'demo':
                return <SignUpForDemo onSuccess={onClose} />;
            case 'webinar':
                return <SignUpForWebinar onSuccess={onClose} />;
            default:
                return <SignUpForm onSuccess={onClose} />;
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                                        {getTitle()}
                                    </Dialog.Title>
                                    <button
                                        onClick={onClose}
                                        className="text-gray-400 hover:text-gray-500 transition-colors p-1 hover:bg-gray-100 rounded-full"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>

                                {getForm()}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}